import { Dispatch, SetStateAction, useCallback } from 'react';
import { calculateGiaTriConLai, calculateGiaTriKH1Ky, calculateNgayKetThucKh } from '../../../utils/calc-util';
import { TSCCRow } from './useTSCCRows';

interface SyncOptions {
  detailRows: MuaHangRecord[];
  enabled: boolean;
  setTSCCRows: Dispatch<SetStateAction<TSCCRow[]>>;
  invoiceDate?: string;
}

interface SyncTSCCRowsOptions {
  prevRows: TSCCRow[];
  detailRows: MuaHangRecord[];
  invoiceDate?: string;
  codeDate?: Date;
}

const areRowsEqual = (left: TSCCRow[], right: TSCCRow[]) => {
  if (left.length !== right.length) return false;

  return left.every((leftRow, index) => {
    const rightRow = right[index];
    if (!rightRow) return false;

    return Object.keys({ ...leftRow, ...rightRow }).every(key => leftRow[key] === rightRow[key]);
  });
};

const getAssetTypeId = (row: MuaHangRecord) => row?.ma_lts_data?.uuid || row?.ma_lts || '';
const getAssetTypeCode = (row: MuaHangRecord) => row?.ma_lts_data?.ma_lts || row?.ma_lts || '';

const isLoadedRowForDetail = (row: TSCCRow, detailRow: MuaHangRecord, generatedAssetCode: string) => {
  if (row.detail_uuid) return false;
  if (row.ma_ts && row.ma_ts === generatedAssetCode) return true;

  const rowAssetType = getAssetTypeId(row) || getAssetTypeCode(row);
  const detailAssetType = getAssetTypeId(detailRow) || getAssetTypeCode(detailRow);
  const lineMatches =
    row.detail_line === detailRow?.line || row.line_ct === detailRow?.line || row.line === detailRow?.line;

  return Boolean(lineMatches && rowAssetType && rowAssetType === detailAssetType);
};

export function syncTSCCRows({
  prevRows,
  detailRows,
  invoiceDate = '',
  codeDate = new Date()
}: SyncTSCCRowsOptions): TSCCRow[] {
  const year = codeDate.getFullYear().toString().slice(-2);
  const month = (codeDate.getMonth() + 1).toString().padStart(2, '0');
  const counters: Record<string, number> = {};
  const matchedRows = new Set<TSCCRow>();

  const syncedRows = (detailRows || [])
    .filter(row => getAssetTypeCode(row))
    .map((detailRow: MuaHangRecord, index: number) => {
      const maLtsCode = getAssetTypeCode(detailRow);
      if (!maLtsCode) return null;

      const order = (counters[maLtsCode] || 0) + 1;
      counters[maLtsCode] = order;

      const generatedAssetCode = `${maLtsCode}.${year}${month}.${order.toString().padStart(4, '0')}`;
      const detailUuid = detailRow?.uuid || `detail-${index}`;

      const existing =
        prevRows.find(row => row.detail_uuid === detailUuid) ||
        prevRows.find(row => isLoadedRowForDetail(row, detailRow, generatedAssetCode));

      if (existing) {
        matchedRows.add(existing);
      }

      const finalAssetCode = existing?.ma_ts || generatedAssetCode;
      const purchaseDate = invoiceDate || existing?.ngay_mua || null;
      const depreciationStartDate = detailRow?.ngay_kh0 || invoiceDate || existing?.ngay_kh0 || purchaseDate || null;
      const depreciationPeriods = detailRow?.so_ky_kh || existing?.so_ky_kh || null;
      const calculatedDepreciationEndDate = calculateNgayKetThucKh(depreciationStartDate, Number(depreciationPeriods));
      const depreciationEndDate = existing?.ngay_kh_kt || calculatedDepreciationEndDate;
      const foreignCurrencyAmount = detailRow?.tien_nt || 0;
      const originalCostForeignCurrency = foreignCurrencyAmount || existing?.nguyen_gia_nt || 0;
      const depreciationValueNt = existing?.gt_da_kh_nt || 0;
      const depreciationValue = calculateGiaTriKH1Ky(originalCostForeignCurrency, Number(depreciationPeriods));
      const remainingValueForeignCurrency = calculateGiaTriConLai(originalCostForeignCurrency, depreciationValueNt);
      const quantity = detailRow?.so_luong || existing?.so_luong;
      const dvt =
        detailRow?.dvt_data?.dvt ||
        detailRow?.dvt ||
        detailRow?.ma_dv_data?.dvt_data?.dvt ||
        detailRow?.ma_dv_data?.dvt ||
        existing?.dvt;

      return {
        ...existing,
        uuid: existing?.uuid || detailUuid || String(Math.random()),
        detail_uuid: detailUuid,
        detail_line: detailRow?.line,
        ma_ts: finalAssetCode,
        ten_ts: detailRow?.ma_dv_data?.ten_dv || detailRow?.ten_dv || detailRow?.dien_giai || existing?.ten_ts || '',
        ma_lts_data: detailRow?.ma_lts_data || existing?.ma_lts_data || null,
        tk_ts_data: detailRow?.ma_lts_data?.tk_ts_data || existing?.tk_ts_data || null,
        tk_kh_data: detailRow?.ma_lts_data?.tk_kh_data || existing?.tk_kh_data || null,
        tk_cp_data: detailRow?.ma_lts_data?.tk_cp_data || existing?.tk_cp_data || null,
        ma_phi_data: detailRow?.ma_phi_data || detailRow?.ma_dv_data?.ma_phi_data || existing?.ma_phi_data || null,
        ma_lts: maLtsCode,
        ngay_mua: purchaseDate,
        ngay_kh0: depreciationStartDate,
        so_ky_kh: depreciationPeriods,
        ngay_kh_kt: depreciationEndDate,
        nguyen_gia_nt: originalCostForeignCurrency,
        gt_cl_nt: remainingValueForeignCurrency,
        gt_kh_ky_nt: depreciationValue,
        so_luong: quantity,
        dvt_data: detailRow?.dvt_data || detailRow?.ma_dv_data?.dvt_data || existing?.dvt_data || null,
        dvt: dvt
      };
    })
    .filter(Boolean) as TSCCRow[];

  const manualRows = (prevRows || []).filter(row => !row.detail_uuid && !matchedRows.has(row));
  const nextRows = [...manualRows, ...syncedRows];

  return nextRows.map((row, idx) => ({
    ...row,
    line: idx + 1
  }));
}

export function useSyncTSCC({ detailRows, enabled, setTSCCRows, invoiceDate = '' }: SyncOptions) {
  return useCallback(() => {
    if (!enabled) return;

    setTSCCRows(prevRows => {
      const finalRows = syncTSCCRows({ prevRows, detailRows, invoiceDate });

      return areRowsEqual(prevRows, finalRows) ? prevRows : finalRows;
    });
  }, [detailRows, enabled, invoiceDate, setTSCCRows]);
}
